import React from 'react';

import { getRolloutList } from '../Services/SearchFilesService';
import { normalizeRollout } from '../Components/RolloutCatalog/rolloutCatalogUtils';
import { getTagNames } from '@/Modules/Core/Components/Tags/tagUtils';

const FETCH_BATCH_SIZE = 50;
const MAX_FETCH_PAGES = 20;
const DEFAULT_SORT = ['-created_date'];
const SERVER_SORT_FIELD_MAP = {
  rolloutName: 'rollout_name',
  description: 'rollout_description',
  createdAt: 'created_date',
  bugs: 'total_bugs',
};

const buildServerSort = (sorting) => {
  const [activeSort] = Array.isArray(sorting) ? sorting : [];
  const field = SERVER_SORT_FIELD_MAP[activeSort?.id];

  if (!field) {
    return DEFAULT_SORT;
  }

  return [activeSort?.desc ? `-${field}` : field];
};

const buildServerSearchFilters = (searchTerm) => {
  const normalizedQuery = String(searchTerm ?? '').trim();

  if (!normalizedQuery) {
    return [];
  }

  return [
    {
      ColumnName: 'rollout_name',
      Op: 'contains',
      ColumnValue: normalizedQuery,
    },
  ];
};

const buildServerTagFilters = (selectedTags) => {
  const tagNames = getTagNames(selectedTags);

  if (tagNames.length === 0) {
    return [];
  }

  return tagNames.map((tagName) => ({
    ColumnName: 'tags',
    Op: 'contains',
    ColumnValue: tagName,
  }));
};

const fetchRolloutBatch = async ({
  filter,
  tags,
  sort,
  signal,
  projectId,
  versionId,
}) => {
  let page = 1;
  let total = 0;
  let aggregatedRollouts = [];
  let hasMore = true;

  while (hasMore && page <= MAX_FETCH_PAGES) {
    const response = await getRolloutList({
      limit: FETCH_BATCH_SIZE,
      offset: page,
      filter,
      tags,
      sort,
      signal,
      projectId,
      versionId,
    });

    if (response?.isSuccess === false && !response?.rollouts) {
      throw new Error(response?.message || 'Failed to load rollouts.');
    }

    const pageItems = Array.isArray(response?.rollouts) ? response.rollouts : [];
    aggregatedRollouts = aggregatedRollouts.concat(pageItems);

    const totalFromApi = Number(response?.TotalCount ?? aggregatedRollouts.length);

    total = Number.isFinite(totalFromApi)
      ? totalFromApi
      : aggregatedRollouts.length;

    hasMore =
      pageItems.length === FETCH_BATCH_SIZE &&
      aggregatedRollouts.length < total;
    page += 1;
  }

  return {
    items: aggregatedRollouts,
    total,
  };
};

const useRolloutCatalog = ({
  projectId,
  versionId,
  searchTerm,
  selectedTags,
  sorting,
}) => {
  const [rollouts, setRollouts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [reportedTotal, setReportedTotal] = React.useState(0);
  const hasLoadedRef = React.useRef(false);
  const requestIdRef = React.useRef(0);
  const activeControllerRef = React.useRef(null);

  const refreshRollouts = React.useCallback(async () => {
    if (!projectId || !versionId) {
      activeControllerRef.current?.abort();
      activeControllerRef.current = null;
      setRollouts([]);
      setReportedTotal(0);
      setLoading(false);
      setRefreshing(false);
      hasLoadedRef.current = false;
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    activeControllerRef.current?.abort();
    const requestController = new AbortController();
    activeControllerRef.current = requestController;

    if (hasLoadedRef.current) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setErrorMessage('');

    try {
      const sort = buildServerSort(sorting);
      const tags = getTagNames(selectedTags);
      const filter = [
        ...buildServerSearchFilters(searchTerm),
        ...buildServerTagFilters(selectedTags),
      ];
      const response = await fetchRolloutBatch({
        filter,
        tags,
        sort,
        signal: requestController.signal,
        projectId,
        versionId,
      });

      if (requestId !== requestIdRef.current) {
        return;
      }

      const aggregatedRollouts = response.items;

      const dedupedRollouts = Array.from(
        new Map(
          aggregatedRollouts.map((item, index) => {
            const normalized = normalizeRollout(item, index);
            return [normalized.rolloutName || normalized.key, normalized];
          })
        ).values()
      );

      setRollouts(dedupedRollouts);
      setReportedTotal(dedupedRollouts.length);
      hasLoadedRef.current = true;
    } catch (error) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      if (error?.name === 'AbortError') {
        return;
      }

      if (!hasLoadedRef.current) {
        setRollouts([]);
        setReportedTotal(0);
      }

      setErrorMessage(error?.message || 'Unable to load rollouts.');
    } finally {
      if (requestId === requestIdRef.current) {
        if (activeControllerRef.current === requestController) {
          activeControllerRef.current = null;
        }
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [projectId, searchTerm, selectedTags, sorting, versionId]);

  React.useEffect(() => {
    refreshRollouts();
  }, [refreshRollouts]);

  React.useEffect(
    () => () => {
      activeControllerRef.current?.abort();
    },
    []
  );

  return {
    errorMessage,
    loading,
    refreshing,
    rollouts,
    refreshRollouts,
    reportedTotal,
  };
};

export default useRolloutCatalog;
